import React, { useCallback, useEffect, useState } from 'react';

type WallpaperSlide = {
  base: string;
  alt: string;
};

/** Full-bleed campus wallpapers — cinematic, warm, academic. */
const SLIDES: WallpaperSlide[] = [
  {
    base: 'https://images.unsplash.com/photo-1562774053-701939374585',
    alt: 'University campus building at dusk',
  },
  {
    base: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f',
    alt: 'Campus courtyard and academic halls',
  },
  {
    base: 'https://images.unsplash.com/photo-1509062522246-3755977927d7',
    alt: 'Students learning in a bright classroom',
  },
  {
    base: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6',
    alt: 'Library shelves with books',
  },
  {
    base: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b',
    alt: 'Classroom desks ready for the day',
  },
];

const INTERVAL_MS = 7500;

/** Preload lead time for the upcoming wallpaper — well inside INTERVAL_MS. */
const PRELOAD_DELAY_MS = 2000;

const WIDTHS = [960, 1440, 1920];

function wallpaperUrl(base: string, width: number) {
  return `${base}?auto=format&fit=crop&q=82&w=${width}`;
}

function wallpaperSrcSet(base: string) {
  return WIDTHS.map((w) => `${wallpaperUrl(base, w)} ${w}w`).join(', ');
}

/** Cycling full-bleed wallpaper for the landing hero. */
export function HeroWallpaper() {
  const [active, setActive] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [mounted, setMounted] = useState<number[]>([0]);

  const mountSlide = useCallback((index: number) => {
    setMounted((prev) => (prev.includes(index) ? prev : [...prev, index]));
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (reduceMotion || SLIDES.length < 2) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion]);

  // Only the current wallpaper is in the DOM at first paint; the next one is
  // fetched mid-cycle so the crossfade has a decoded image without putting five
  // full-bleed JPEGs in front of the hero's first render.
  useEffect(() => {
    mountSlide(active);
    if (reduceMotion || SLIDES.length < 2) return;
    const id = window.setTimeout(
      () => mountSlide((active + 1) % SLIDES.length),
      PRELOAD_DELAY_MS
    );
    return () => window.clearTimeout(id);
  }, [active, reduceMotion, mountSlide]);

  return (
    <div className="hero-wallpaper" aria-hidden>
      {SLIDES.map((slide, i) => (
        <div
          key={slide.base}
          className={`hero-wallpaper__slide${i === active ? ' is-active' : ''}${
            reduceMotion ? ' hero-wallpaper__slide--static' : ''
          }`}
        >
          {mounted.includes(i) && (
            <img
              className="hero-wallpaper__img"
              src={wallpaperUrl(slide.base, 1920)}
              srcSet={wallpaperSrcSet(slide.base)}
              sizes="100vw"
              alt=""
              decoding="async"
            />
          )}
        </div>
      ))}
      <div className="hero-wallpaper__scrim" />
      <div className="hero-wallpaper__vignette" />
      <div className="hero-wallpaper__grain" />
      <div className="hero-wallpaper__progress">
        {SLIDES.map((_, i) => (
          <span
            key={i}
            className={`hero-wallpaper__tick${i === active ? ' is-active' : ''}${
              reduceMotion ? ' is-static' : ''
            }`}
          />
        ))}
      </div>
    </div>
  );
}
